import type { CityConfig } from "@/pages/city-template/types";

export const hazletonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Hazleton",
  state: "Pennsylvania",
  stateAbbr: "PA",
  slug: "hazleton",
  route: "/cities/hazleton",
  title: "Emergency Passport Hazleton, PA — Expedited Same-Day Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Hazleton, PA — as fast as 24 hours. Same-day courier processing for Luzerne County residents, Penn State Hazleton students, and Northeastern Pennsylvania travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Hazleton, Pennsylvania",
  heroSubtitle: "Flying from AVP for a family trip to the Dominican Republic and realized your passport expired? Penn State Hazleton student heading abroad for a semester? Fast Passport Center helps Hazleton and Luzerne County residents get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Hazleton%20Pennsylvania%20cityscape%20with%20historic%20downtown%20Broad%20Street%2C%20classic%20brick%20commercial%20buildings%20and%20church%20spires%20rising%20above%20the%20streetscape%2C%20Pocono%20Mountains%20foothills%20visible%20in%20the%20background%20with%20soft%20rolling%20ridges%2C%20vibrant%20multicultural%20community%20atmosphere%2C%20warm%20afternoon%20sunlight%20casting%20long%20shadows%2C%20photorealistic%20small%20city%20photography%20with%20rich%20colors%20and%20detail%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=hazleton-hero-01&orientation=landscape",
  heroImageAlt: "Hazleton Pennsylvania downtown Broad Street with Pocono Mountains foothills in background",
  geoPosition: "40.9584;-75.9747",
  geoPlacename: "Hazleton, Pennsylvania",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "200 Chestnut St, Philadelphia, PA 19106",
    distance: "About 110 miles southeast — 2 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Hazleton", zips: "18201, 18202" },
    { name: "West Hazleton", zips: "18202" },
    { name: "Hazle Township", zips: "18202" },
    { name: "McAdoo", zips: "18237" },
    { name: "Freeland", zips: "18224" },
    { name: "Weatherly", zips: "18255" },
    { name: "Sugarloaf", zips: "18249" },
    { name: "Conyngham", zips: "18219" },
    { name: "Drums", zips: "18222" },
    { name: "Nuremberg", zips: "18241" },
    { name: "Tamaqua", zips: "18252" },
    { name: "Beaver Meadows", zips: "18216" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home in Hazleton or your dorm at Penn State Hazleton in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Luzerne County", desc: "FedEx and UPS locations throughout Hazleton and the Luzerne County area offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission — critical for Hazleton residents with last-minute travel plans." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the Philadelphia Passport Agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Luis G.",
      location: "Hazleton, PA",
      rating: 5,
      text: "I needed an emergency passport to fly to the Dominican Republic for a family emergency. Fast Passport Center got it done in 3 days. They were professional, compassionate, and incredibly fast. I'm so grateful.",
      service: "Emergency Passport",
    },
    {
      name: "Ashley P.",
      location: "West Hazleton, PA",
      rating: 5,
      text: "My daughter is a Penn State Hazleton student who got into a summer program in Costa Rica. With only 3 weeks, Fast Passport Center got her first passport in 7 days. The step-by-step guide made everything easy.",
      service: "New Passport",
    },
    {
      name: "Michael W.",
      location: "Drums, PA",
      rating: 5,
      text: "Booked an anniversary trip to Europe and realized my passport had expired. Fast Passport Center renewed it in 6 days. Shipped from the UPS in Hazleton and had it back in plenty of time. Great experience.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "hazleton-post",
      name: "Hazleton Post Office",
      type: "USPS Passport Acceptance",
      address: "2 N Church St",
      city: "Hazleton",
      state: "PA",
      zip: "18201",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Hazleton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance location in Hazleton. Appointment required — book online. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "luzerne-county-courthouse",
      name: "Luzerne County Courthouse",
      type: "County Passport Acceptance",
      address: "200 N River St",
      city: "Wilkes-Barre",
      state: "PA",
      zip: "18711",
      phone: "570-825-1500",
      hours: "Mon–Fri 8:30am–4:30pm",
      distance: "Wilkes-Barre — about 25 miles north",
      website: "https://www.luzernecounty.org/",
      websiteLabel: "Luzerne County Website",
      notes: "Luzerne County courthouse passport acceptance in Wilkes-Barre. Call ahead for hours and appointment requirements.",
      icon: "ri-government-line",
    },
    {
      id: "freeland-post",
      name: "Freeland Post Office",
      type: "USPS Passport Acceptance",
      address: "500 Centre St",
      city: "Freeland",
      state: "PA",
      zip: "18224",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Freeland — about 7 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Freeland option for eastern Hazleton area and southern Luzerne County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "tamaqua-post",
      name: "Tamaqua Post Office",
      type: "USPS Passport Acceptance",
      address: "35 N Railroad St",
      city: "Tamaqua",
      state: "PA",
      zip: "18252",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Tamaqua — about 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tamaqua option for southern Hazleton area and Schuylkill County border residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Hazleton before international travel?",
      a: "Yes. Through Fast Passport Center, Hazleton travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility. Start your order online today.",
    },
    {
      q: "How fast can I renew my passport in Hazleton?",
      a: "Fast passport renewal in Hazleton is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Most renewals require no office visit.",
    },
    {
      q: "Can Penn State Hazleton students get expedited passports for study abroad?",
      a: "Absolutely. We help Penn State Hazleton and Luzerne County Community College students get passports fast for study abroad programs. Start early — don't wait until the last week before your program. We can meet tight semester deadlines.",
    },
    {
      q: "Does Hazleton have a regional passport agency?",
      a: "Hazleton does not have a regional passport agency. The closest is the Philadelphia Passport Agency (about 110 miles southeast). It requires appointments booked weeks in advance and proof of travel within 14 days. Fast Passport Center is the faster, more convenient solution for most Northeastern Pennsylvania residents.",
    },
    {
      q: "Can I expedite a child passport in Hazleton?",
      a: "Yes. Child passport expediting in Hazleton is possible through Fast Passport Center. Child passports for minors 15 and under require an in-person acceptance step with parental consent. We provide a clear checklist and handle expedited processing after the acceptance step.",
    },
    {
      q: "What airports do Hazleton travelers typically use?",
      a: "Wilkes-Barre/Scranton International Airport (AVP — about 35 miles north) and Lehigh Valley International Airport (ABE — about 50 miles southeast) are the primary airports for Hazleton travelers. Philadelphia International (PHL) is about 110 miles away for direct international flights.",
    },
    {
      q: "How long does a standard passport take in Hazleton?",
      a: "If you apply through a standard post office acceptance facility in Hazleton, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner, you need expedited processing through Fast Passport Center.",
    },
    {
      q: "Can I get a passport renewal in Hazleton without visiting an office?",
      a: "Yes. Most Hazleton-area adults renewing a passport can do so entirely by mail. Start your order online, complete documents at home, and ship overnight from any FedEx or UPS in Hazleton or the Luzerne County area.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Hazleton", address: "10 W Broad St, Hazleton, PA 18201", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/store-locator" },
    { name: "CVS — Hazleton", address: "300 W Broad St, Hazleton, PA 18201", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "Walmart — Hazleton", address: "761 Airport Rd, Hazleton, PA 18202", hours: "Daily 6 AM – 11 PM", website: "https://www.walmart.com/store/finder" },
    { name: "The UPS Store — Hazleton", address: "1000 N Church St, Hazleton, PA 18201", hours: "Mon–Fri 8 AM – 6 PM, Sat 9 AM – 2 PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "Walgreens — West Hazleton", address: "600 S Poplar St, Hazleton, PA 18201", hours: "Daily 8 AM – 9 PM", website: "https://www.walgreens.com/store-locator" },
  ],
  dmvLocations: [
    {
      name: "Hazleton PennDOT Driver License Center",
      address: "1052 S Church St, Hazleton, PA 18201",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Hazleton PennDOT center. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Wilkes-Barre PennDOT Driver License Center",
      address: "1085 Hanover St, Wilkes-Barre, PA 18706",
      hours: "Tue–Sat 8:30 AM – 4:15 PM",
      note: "Wilkes-Barre PennDOT option for northern Luzerne County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "AVP",
      name: "Wilkes-Barre/Scranton International Airport",
      distance: "About 35 miles north — 40 minutes drive",
      address: "100 Terminal Rd, Avoca, PA 18641",
      note: "AVP is the closest commercial airport for Hazleton travelers, offering connections to major hubs for international travel. Passport required for all international connections.",
      flightUrl: "https://www.flyavp.com/",
      flightLabel: "AVP Airport Website",
      featured: true,
    },
    {
      code: "ABE",
      name: "Lehigh Valley International Airport",
      distance: "About 50 miles southeast — 1 hour drive",
      address: "3311 Airport Rd, Allentown, PA 18109",
      note: "ABE offers additional domestic connections and is a convenient option for Hazleton travelers heading south. Passport required for international itineraries.",
      flightUrl: "https://www.flyabe.com/",
      flightLabel: "ABE Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from AVP or ABE in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Hazleton and Northeastern Pennsylvania.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Hazleton and Luzerne County travelers renew fast — no office visit required for most renewals.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Hazleton", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Hazleton travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Hazleton families — minors under 16 require in-person acceptance and parental consent.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Hazleton residents and Penn State Hazleton students heading abroad.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all expedited.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Hazleton, Pennsylvania travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Hazleton and Luzerne County residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Hazleton, PA residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Hazleton travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Hazleton families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Hazleton, Pennsylvania." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Hazleton travelers often underestimate processing times. Government processing runs 6–13 weeks — longer during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and catch these issues before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. We tell you exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Hazleton runs 6–13 weeks. If your trip is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly. Middle names and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Hazleton travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Hazleton", description: "Drop off at any FedEx or UPS in Hazleton or the Luzerne County area. Most locations are open evenings." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins", "Life-or-death emergencies only", "Must travel to Philadelphia (2 hours)", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Philadelphia", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Philadelphia (2 hours each way)", fpc: "Ship documents overnight from Hazleton" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled", fpc: "Predictable processing based on option" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Wilkes-Barre", route: "/cities/wilkes-barre" },
    { name: "Fast passport renewal near Scranton", route: "/cities/scranton" },
    { name: "Emergency passport help for Luzerne County travelers", route: "/states/pennsylvania" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Hazleton or the Luzerne County area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Hazleton and all of Luzerne County remotely",
  serviceAreaIntro: "Whether you are in downtown Hazleton, West Hazleton, Freeland, Drums, or anywhere in the Hazleton area, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Hazleton and southern Luzerne County zip codes. Anywhere in the Pocono foothills region — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Hazleton as simple as possible. Most Hazleton clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Hazleton travelers. Fast Passport Center reviews your documents before submission to catch these issues.",
  testimonialsIntro: "Real reviews from Hazleton, West Hazleton, and Drums travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Hazleton and Luzerne County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/pennsylvania",
  statePageName: "Pennsylvania",
  dmvWebsite: "https://www.dmv.pa.gov/",
  dmvLabel: "PennDOT — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from Pennsylvania Department of Health Vital Records for Luzerne County.", url: "https://www.health.pa.gov/topics/certificates/Pages/Birth-Certificates.aspx" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Pennsylvania Department of Health Vital Records.", url: "https://www.health.pa.gov/topics/certificates/Pages/Death-Certificates.aspx" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Luzerne County Register of Wills.", url: "https://www.luzernecounty.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through Luzerne County Prothonotary.", url: "https://www.luzernecounty.org/" },
    { icon: "ri-global-line", label: "VitalChek", desc: "Order Pennsylvania birth, death, marriage, and divorce certificates online through VitalChek for convenience.", url: "https://www.vitalchek.com/" },
  ],
};